import { clearFlow, track, updateFlow } from 'lib/sdks/analytics';
import type { PrettifyType } from 'lib/utils/typescript';
import type { ComponentProps, JSXElementConstructor, JSX } from 'react';
import { useState } from 'react';

export const createFlowScreen = <
  C extends keyof JSX.IntrinsicElements | JSXElementConstructor<any>,
>(
  component: C,
  props: PrettifyType<Omit<ComponentProps<C>, 'onBack' | 'onDismiss'>>,
) => ({
  component,
  props,
});

export const useFlow = <Screen extends string | number>({
  initial,
  trackingStepMap,
  flowId,
}: {
  initial?: Screen;
  trackingStepMap?: Partial<Record<Screen, number>>;
  flowId: number;
}) => {
  const [steps, setStepState] = useState<Screen[]>(initial ? [initial] : []);

  const currentStep = steps.slice(-1)[0];

  const setStep = (
    nextStep: Screen,
    {
      cleared,
      sendTrackEvent = true,
    }: { cleared?: boolean; sendTrackEvent?: boolean } = {},
  ) => {
    const trackingStep = trackingStepMap && trackingStepMap[nextStep];

    track('job.view_change', { view: nextStep });

    if (sendTrackEvent && trackingStep) {
      cleared
        ? clearFlow(flowId, undefined, trackingStep)
        : updateFlow(flowId, trackingStep);
    }

    setStepState(prev => [...prev, nextStep]);
  };

  const goToPreviousStep = () => {
    setStepState(prev => prev.slice(0, -1));
  };

  return {
    steps,
    currentStep,
    setStep,
    goToPreviousStep,
  };
};
